import type { ReactElement } from 'react';
import type { TFunction } from 'i18next';
import type { QuestObjective } from '../../../models/apiTypes';
import type { DailyQuestStartTarget, DailyQuestView } from '../../../models/quest';
import { CLAIMED_QUEST_PROTECTION_MS, globalClaimedQuestTimestamps } from '../constants';
import { optimisticallyClaimedRewards } from '../../../utils/quest/optimisticQuestState';

const isRewardClaimedLocally = (id: number | null | undefined): boolean =>
  id != null && optimisticallyClaimedRewards.has(id);
import { cn } from '../../cn';


export interface DailyQuestCardClaimButtonProps {
  quest: DailyQuestView;
  t: TFunction;
  isXFollowQuest: (questId: number) => boolean;
  isXQuestOnModeration: (questId: number) => boolean;
  needsStartButton: (quest: DailyQuestStartTarget) => boolean;
  handleClaimReward: (userQuestId: number) => Promise<void>;
  claimingQuestIds: Set<number>;
}

export default function DailyQuestCardClaimButton({
  quest,
  t,
  isXFollowQuest,
  isXQuestOnModeration,
  needsStartButton,
  handleClaimReward,
  claimingQuestIds,
}: DailyQuestCardClaimButtonProps): ReactElement | null {
// Спеціальна обробка для квестів відвідування локацій
// Якщо квест не завершений - не показуємо claim кнопку тут (показується кнопка-перехід вище)
const visitObjective = quest.objectives?.find((obj: QuestObjective) => obj.type === 'visit_static_map_object');
if (visitObjective && !quest.isFinished) {
  return null;
}

// Спеціальна обробка для квестів тапінгу (quest_id 5, 106, 107)
// Якщо квест не завершений - не показуємо claim кнопку тут (показується кнопка на мушлю вище)
if ((quest.questId === 5 || quest.questId === 106 || quest.questId === 107) && !quest.isFinished) {
  return null;
}

// Спеціальна обробка для квесту підписки на Telegram (quest_id 6)
// Якщо квест не завершений - не показуємо claim кнопку тут (показується кнопка "ПІДПИСАТИСЯ" вище)
if (quest.questId === 6 && !quest.isFinished) {
  return null;
}

// Спеціальна обробка для квесту підписки на Telegram чат (quest_id 108)
// Якщо квест не завершений - не показуємо claim кнопку тут (показується кнопка "Join Chat" вище)
if (quest.questId === 108 && !quest.isFinished) {
  return null;
}

// Спеціальна обробка для квесту написання боту (quest_id 172)
// Якщо квест не завершений - не показуємо claim кнопку тут (показується кнопка в бот вище)
if (quest.questId === 172 && !quest.isFinished) {
  return null;
}


// Спеціальна обробка для квестів виборів мера (139, 140, 141)
// Якщо квест не завершений - не показуємо claim кнопку тут (показується кнопка на ратушу вище)
if ((quest.questId === 139 || quest.questId === 140 || quest.questId === 141) && !quest.isFinished) {
  return null;
}

// Спеціальна обробка для квесту запрошення друзів (quest_id 200)
// Якщо квест не завершений - не показуємо claim кнопку тут (показується кнопка "Invite my friends" вище)
if (quest.questId === 200 && !quest.isFinished) {
  return null;
}

// Спеціальна обробка для квесту дуелей (quest_id 207)
// Якщо квест не завершений - не показуємо claim кнопку тут (показується кнопка на дуелі вище)
if (quest.questId === 207 && !quest.isFinished) {
  return null;
}

// Спеціальна обробка для партнерського квесту (quest_id 7)
if (quest.questId === 7 && !quest.isFinished) {
  return null;
}

// Спеціальна обробка для квестів підписки на X (quest_id 208, 209)
// Якщо квест на модерації або ще не пройшов через флоу - не показуємо claim кнопку тут
// (вона вже оброблена вище)
if (isXFollowQuest(quest.questId)) {
  // Якщо винагорода вже забрана - показуємо стан "claimed" (продовжуємо до звичайної логіки)
  if (quest.isRewardClaimed) {
    // Продовжуємо до звичайної логіки, яка правильно покаже стан "claimed"
  }
  // Якщо бекенд позначив квест як завершений - показуємо claim кнопку (продовжуємо нижче)
  else if (quest.isFinished) {
    // Продовжуємо до звичайної логіки claim кнопки
  }
  // Якщо квест ще не завершений - не показуємо claim (оброблено вище через кнопки ПІДПИСАТИСЯ/МОДЕРАЦІЯ)
  else {
    return null;
  }
}

// Якщо потрібна кнопка START - не показуємо claim
// Але не для квестів 5, 6, 106, 107, 108 та 172, бо вони мають свої спеціальні кнопки вище
if (needsStartButton(quest) && quest.questId !== 5 && quest.questId !== 6 && quest.questId !== 106 && quest.questId !== 107 && quest.questId !== 108 && quest.questId !== 172) {
  return null;
}

// Для X квестів не показуємо claim поки на модерації або не завершений
if (isXFollowQuest(quest.questId)) {
  if (!quest.isFinished || isXQuestOnModeration(quest.questId)) {
    return null;
  }
}

// Показуємо кнопку claim для звичайних квестів або завершених ad quest
// Перевіряємо захист кнопки (timestamp менше 10 секунд)
const hasActiveProtection = isRewardClaimedLocally(quest.userQuestId) || !!(quest.userQuestId && globalClaimedQuestTimestamps.has(quest.userQuestId) &&
  // eslint-disable-next-line react-hooks/purity -- claim-protection time window, intentional time read
  Date.now() - (globalClaimedQuestTimestamps.get(quest.userQuestId) || 0) < CLAIMED_QUEST_PROTECTION_MS);

const isDisabled = !quest.isFinished || quest.isRewardClaimed || hasActiveProtection || claimingQuestIds.has(quest.userQuestId || 0) || !quest.userQuestId;
return (
<button
  onClick={() => quest.userQuestId && handleClaimReward(quest.userQuestId)}
  disabled={isDisabled}
  className={cn(
    'flex w-full items-center justify-center rounded-[34.925px] border border-accent bg-white px-3xl py-sm opacity-100 shadow-card-raised',
    isDisabled ? 'cursor-not-allowed' : 'cursor-pointer',
  )}
>
  {!quest.isFinished ? (
    <span className="[font-family:Lato,sans-serif] text-[14px] font-bold text-accent">
      {t("IN PROGRESS")}
    </span>
  ) : quest.isRewardClaimed || hasActiveProtection ? (
    <img
      src="/ui/daily-quests/daily-quests-claimed.png"
      alt="Claimed"
      className="h-6 w-6 object-contain"
    />
  ) : (
    <img
      src="/ui/daily-quests/daily-quests-pearls-claim.png"
      alt="Claim reward"
      className="h-6 w-6 object-contain"
    />
  )}
</button>
);

}
